import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getTranslatedField } from '@/lib/translationHelpers';
import { FlagIcon } from '@/components/FlagIcon';
import type { Product } from '@/hooks/useProducts';

const countryToFlag: Record<string, string> = { SE: 'sv', NO: 'no', DK: 'da', FI: 'fi' };

interface ProductGridCardProps {
  product: Product;
  categoryIcon: React.ElementType;
  categoryLabel: string;
  lang: string;
  formatPrice: (price: number, currency: string) => string;
  updateFrequencyLabels: Record<string, string>;
}

export default function ProductGridCard({
  product,
  categoryIcon: CategoryIcon,
  categoryLabel,
  lang,
  formatPrice,
  updateFrequencyLabels,
}: ProductGridCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="flex flex-col hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-secondary/10 flex items-center justify-center">
              <CategoryIcon className="w-4 h-4 text-secondary" />
            </div>
            <Badge variant="secondary" className="text-xs">
              {categoryLabel}
            </Badge>
          </div>
          {product.sku && (
            <Badge variant="outline" className="text-xs font-mono">
              {product.sku}
            </Badge>
          )}
        </div>
        <h3 className="font-semibold text-lg mt-3 line-clamp-2">
          {getTranslatedField(product, 'name', lang)}
        </h3>
      </CardHeader>
      <CardContent className="flex-1">
        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
          {getTranslatedField(product, 'short_description', lang)}
        </p>
        <div className="space-y-2 text-sm">
          {product.file_format && product.file_format.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('shop.format')}</span>
              <span className="font-medium">{product.file_format.join(', ')}</span>
            </div>
          )}
          {product.update_frequency && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground flex items-center gap-1">
                {t('shop.dataUpdatedFrequency')}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-popover text-popover-foreground">
                      <p>{t('shop.dataUpdatedTooltip')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
              <span className="font-medium">{t(updateFrequencyLabels[product.update_frequency])}</span>
            </div>
          )}
          {product.row_count && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('dataFiles.specs.rows')}</span>
              <span className="font-medium">{product.row_count}</span>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex items-center justify-between pt-4 border-t">
        {product.available_countries && product.available_countries.length > 0 && (
          <div className="flex items-center gap-1 mr-3">
            {product.available_countries.map((c) => {
              const flag = countryToFlag[c];
              return flag ? <FlagIcon key={c} code={flag} className="w-5 h-3.5" /> : null;
            })}
          </div>
        )}
        <div>
          <p className="text-2xl font-bold text-foreground">
            {formatPrice(product.price, product.currency)}
          </p>
          <p className="text-xs text-muted-foreground">{t('shop.exVat')}</p>
        </div>
        <Button asChild>
          <Link to={`/${lang}/shop/${product.slug}`}>
            {t('common.readMore')}
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
